/**
 * participants contracts — owned by work package api-participants.
 * Request/response schemas of the registration, participants (incl. imports & teams), pairings,
 * caddies and matchplay modules (CLAUDE.md §5). Names are prefixed by area to avoid collisions:
 * PublicRegistration*, Participants*, ParticipantImport*, ParticipantTeam*, Pairings*, Caddies*,
 * Matchplay*.
 */
import { z } from 'zod';
import { Gender, ParticipantStatus, PlayStatus, RegistrationSource, StartType } from '../enums';
import { I18nText } from '../i18n';
import { RegistrationSettingsSchema } from '../config/registration';
import { MatchViewSchema } from './leaderboard';
import { TeeChoicesSchema } from './tee';

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:mm');
const reason = z.string().trim().min(1).max(500);

// =============================================================================================
// Error codes specific to these modules (web: "<ns>:errors.<CODE>" -> "errors:<CODE>")
// =============================================================================================
export const ParticipantsErrorCode = {
  DUPLICATE_REGISTRATION: 'DUPLICATE_REGISTRATION',
  REGISTRATION_CLOSED: 'REGISTRATION_CLOSED',
  REGISTRATION_FULL: 'REGISTRATION_FULL',
  PHONE_INVALID: 'PHONE_INVALID',
  FIELD_REQUIRED: 'FIELD_REQUIRED',
  CATEGORY_INVALID: 'CATEGORY_INVALID',
  BIB_TAKEN: 'BIB_TAKEN',
  PROFILE_ALREADY_LINKED: 'PROFILE_ALREADY_LINKED',
  IMPORT_HAS_ERRORS: 'IMPORT_HAS_ERRORS',
  IMPORT_FILE_INVALID: 'IMPORT_FILE_INVALID',
  ALREADY_IN_TEAM: 'ALREADY_IN_TEAM',
  TEAM_HAS_SCORES: 'TEAM_HAS_SCORES',
  TEAM_NUMBER_TAKEN: 'TEAM_NUMBER_TAKEN',
  GROUP_NUMBER_TAKEN: 'GROUP_NUMBER_TAKEN',
  GROUP_HAS_SCORES: 'GROUP_HAS_SCORES',
  ROUND_HAS_SCORES: 'ROUND_HAS_SCORES',
  COURSE_NOT_IN_ROUND: 'COURSE_NOT_IN_ROUND',
  GROUP_NOT_IN_ROUND: 'GROUP_NOT_IN_ROUND',
  PARTICIPANT_NOT_APPROVED: 'PARTICIPANT_NOT_APPROVED',
  ALREADY_GROUPED: 'ALREADY_GROUPED',
  CADDY_CODE_TAKEN: 'CADDY_CODE_TAKEN',
  CONTEST_ROUND_MISMATCH: 'CONTEST_ROUND_MISMATCH',
  BRACKET_HAS_RESULTS: 'BRACKET_HAS_RESULTS',
  BRACKET_NEEDS_WINNER: 'BRACKET_NEEDS_WINNER',
  MATCH_SIDES_INVALID: 'MATCH_SIDES_INVALID',
  NOT_ENOUGH_COMPETITORS: 'NOT_ENOUGH_COMPETITORS',
} as const;
export type ParticipantsErrorCode =
  (typeof ParticipantsErrorCode)[keyof typeof ParticipantsErrorCode];

// =============================================================================================
// registration (public sign-up)
// =============================================================================================
export const PublicRegistrationCategorySchema = z.object({
  key: z.string(),
  label: I18nText,
  fieldType: z.enum(['SELECT', 'TEXT', 'NUMBER']),
  required: z.boolean(),
  /** derivation != NONE: the server computes the value (PRO_AM: from isPro) — do not ask */
  derivation: z.enum(['NONE', 'GENDER', 'AGE', 'HANDICAP', 'NATIONALITY', 'PRO_AM']),
  options: z.array(z.object({ value: z.string(), label: I18nText })),
});

export const PublicRegistrationClosedReason = z.enum([
  'DISABLED',
  'NOT_OPEN_STATUS',
  'NOT_YET_OPEN',
  'CLOSED',
  'FULL',
]);

export const PublicRegistrationFormSchema = z.object({
  tournament: z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    startDate: z.string(),
    endDate: z.string(),
    timezone: z.string(),
    defaultLocale: z.string(),
    status: z.string(),
    formatKind: z.string(),
  }),
  branding: z
    .object({
      logoUrl: z.string().nullable(),
      primaryColor: z.string(),
      secondaryColor: z.string(),
      accentColor: z.string(),
      headline: I18nText.nullable(),
      footerText: I18nText.nullable(),
    })
    .nullable(),
  settings: RegistrationSettingsSchema,
  categories: z.array(PublicRegistrationCategorySchema),
  capacity: z.number().nullable(),
  /** seats taken (APPROVED + PENDING) */
  taken: z.number(),
  remaining: z.number().nullable(),
  waitlistCount: z.number(),
  open: z.boolean(),
  /** sign-ups are accepted but go to the waiting list */
  willWaitlist: z.boolean(),
  closedReason: PublicRegistrationClosedReason.nullable(),
  /** tees the player may pick (round 1 primary layout); null/absent = no choice offered */
  teeChoices: TeeChoicesSchema.nullable().optional(),
});
export type PublicRegistrationForm = z.infer<typeof PublicRegistrationFormSchema>;

export const PublicRegistrationResultSchema = z.object({
  participantId: z.uuid(),
  fullName: z.string(),
  status: z.enum(['PENDING', 'APPROVED', 'WAITLISTED']),
  waitlistPosition: z.number().nullable(),
  bibNumber: z.number().nullable(),
  /** personal link /p/:accessToken — ALWAYS returned (status / waitlist position / scorecard) */
  playerPortalUrl: z.string(),
});
export type PublicRegistrationResult = z.infer<typeof PublicRegistrationResultSchema>;

// =============================================================================================
// participants
// =============================================================================================
export const ParticipantSummarySchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  bibNumber: z.number().nullable(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  gender: Gender.nullable(),
  birthYear: z.number().nullable(),
  age: z.number().nullable(),
  nationality: z.string().nullable(),
  isPro: z.boolean(),
  handicapIndex: z.number().nullable(),
  fixedHandicap: z.number().nullable(),
  teeBoxId: z.uuid().nullable(),
  status: ParticipantStatus,
  playStatus: PlayStatus,
  playStatusReason: z.string().nullable(),
  waitlistPosition: z.number().nullable(),
  source: RegistrationSource,
  playerProfileId: z.uuid().nullable(),
  /** category key -> value */
  categories: z.record(z.string(), z.string()),
  note: z.string().nullable(),
  registeredAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});
export type ParticipantSummary = z.infer<typeof ParticipantSummarySchema>;

export const ParticipantDetailSchema = ParticipantSummarySchema.extend({
  registrationData: z.unknown().nullable(),
  tee: z.object({ id: z.uuid(), name: z.string(), color: z.string().nullable() }).nullable(),
  groups: z.array(
    z.object({
      roundId: z.uuid(),
      roundNo: z.number(),
      groupId: z.uuid(),
      number: z.number(),
      name: z.string().nullable(),
      startHole: z.number(),
      teeTime: z.string().nullable(),
      courseId: z.uuid(),
    }),
  ),
  team: z.object({ id: z.uuid(), name: z.string(), number: z.number().nullable() }).nullable(),
  playerProfile: z
    .object({ id: z.uuid(), fullName: z.string(), phone: z.string().nullable() })
    .nullable(),
  /** personal link / sign-off code: only for OPERATOR+ (null for VIEWER) */
  accessToken: z.string().nullable(),
  playerPortalUrl: z.string().nullable(),
  signOffCode: z.string().nullable(),
  hasScores: z.boolean(),
});
export type ParticipantDetail = z.infer<typeof ParticipantDetailSchema>;

export const ParticipantsListQuerySchema = z.object({
  /** single status or comma separated list */
  status: z.string().optional(),
  playStatus: z.string().optional(),
  /** registration source (RegistrationSource: WEB_FORM / IMPORT / ADMIN / CLUB), comma separated */
  source: z.string().optional(),
  /** name / phone / bib search */
  q: z.string().max(100).optional(),
  /** `key:value` (repeat the param for several; AND across keys, OR within a key) */
  cat: z.union([z.string(), z.array(z.string())]).optional(),
  /** roundId: only participants without a group in that round */
  ungrouped: z.uuid().optional(),
  sort: z
    .enum(['bib', 'name', 'registeredAt', 'handicap', 'waitlist', 'status'])
    .default('registeredAt'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
});
export type ParticipantsListQuery = z.infer<typeof ParticipantsListQuerySchema>;

export const ParticipantsListResponseSchema = z.object({
  items: z.array(ParticipantSummarySchema),
  nextCursor: z.string().nullable(),
  total: z.number(),
});

const participantFields = {
  fullName: z.string().trim().min(1).max(120),
  phone: z.string().max(40).nullable().optional(),
  email: z.email().nullable().optional(),
  gender: Gender.nullable().optional(),
  birthYear: z.int().min(1900).max(2100).nullable().optional(),
  age: z.int().min(5).max(110).nullable().optional(),
  nationality: z
    .string()
    .regex(/^[A-Za-z]{2}$/)
    .nullable()
    .optional(),
  isPro: z.boolean().optional(),
  handicapIndex: z.number().min(-10).max(54).nullable().optional(),
  fixedHandicap: z.number().min(-10).max(72).nullable().optional(),
  teeBoxId: z.uuid().nullable().optional(),
  bibNumber: z.int().min(1).max(99999).nullable().optional(),
  /** category key -> value (null removes the value) */
  categories: z.record(z.string(), z.string().nullable()).optional(),
  note: z.string().max(1000).nullable().optional(),
};

export const ParticipantCreateSchema = z.object({
  ...participantFields,
  status: z.enum(['PENDING', 'APPROVED', 'WAITLISTED']).default('APPROVED'),
  custom: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
});
export type ParticipantCreate = z.infer<typeof ParticipantCreateSchema>;

export const ParticipantUpdateSchema = z
  .object({ ...participantFields, fullName: participantFields.fullName.optional() })
  .extend({ reason: z.string().max(500).optional() });
export type ParticipantUpdate = z.infer<typeof ParticipantUpdateSchema>;

export const ParticipantDeleteResultSchema = z.object({
  deleted: z.boolean(),
  /** when the participant has scores/audit records it is WITHDRAWN instead of deleted */
  status: ParticipantStatus.nullable(),
});

export const ParticipantStatusChangeSchema = z.object({
  to: z.enum(['PENDING', 'APPROVED', 'WAITLISTED', 'REJECTED', 'WITHDRAWN']),
  reason: z.string().max(500).optional(),
});

export const ParticipantPlayStatusChangeSchema = z.object({
  to: PlayStatus,
  /** required for DQ and WD (REASON_REQUIRED) */
  reason: z.string().max(500).optional(),
});

export const ParticipantsBulkStatusSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(1000),
  to: z.enum(['PENDING', 'APPROVED', 'WAITLISTED', 'REJECTED', 'WITHDRAWN']),
  reason: z.string().max(500).optional(),
});
export const ParticipantsBulkResultSchema = z.object({
  updated: z.number(),
  ids: z.array(z.uuid()),
});

export const ParticipantsWaitlistPromoteSchema = z.object({
  count: z.int().min(1).max(1000).optional(),
});
export const ParticipantsWaitlistPromoteResultSchema = z.object({
  promoted: z.array(z.uuid()),
  remainingCapacity: z.number().nullable(),
});

export const ParticipantAccessTokenResultSchema = z.object({
  accessToken: z.string(),
  playerPortalUrl: z.string(),
  signOffCode: z.string(),
});

export const ParticipantProfileCandidateSchema = z.object({
  id: z.uuid(),
  fullName: z.string(),
  /** full number for PHONE matches, masked ("•••• 567") for name+birth-year matches */
  phone: z.string().nullable(),
  gender: Gender.nullable(),
  birthYear: z.number().nullable(),
  handicapIndex: z.number().nullable(),
  hasUser: z.boolean(),
  matchedBy: z.array(z.enum(['PHONE', 'NAME_BIRTHYEAR'])),
  /** participations of this profile in tournaments of the caller's tenant */
  tenantParticipations: z.number(),
  linkedHere: z.boolean(),
});
export const ParticipantProfileCandidatesSchema = z.object({
  items: z.array(ParticipantProfileCandidateSchema),
});

export const ParticipantLinkProfileSchema = z.object({
  playerProfileId: z.uuid().nullable(),
  reason,
});

export const ParticipantsBibAutoAssignSchema = z.object({
  strategy: z.enum(['BY_NAME', 'BY_REGISTRATION', 'BY_HANDICAP', 'BY_GROUP']),
  startAt: z.int().min(1).max(99999).default(1),
  /** true: renumber every APPROVED participant; false: only those without a bib */
  overwrite: z.boolean().default(false),
});
export const ParticipantsBibAutoAssignResultSchema = z.object({
  assigned: z.number(),
  /** lowest / highest bib given by this run (null when nobody was numbered) */
  first: z.number().nullable(),
  last: z.number().nullable(),
});
export type ParticipantsBibAutoAssignResult = z.infer<typeof ParticipantsBibAutoAssignResultSchema>;

export const ParticipantsRecomputeResultSchema = z.object({ updated: z.number() });

// ---- imports ---------------------------------------------------------------------------------

/**
 * Import target of a column: built-in field, `category:<key>`, `custom:<key>` or null (ignore).
 * Built-ins: fullName, firstName, lastName, phone, email, gender, birthYear, birthDate, age,
 * handicapIndex, nationality, isPro, bibNumber, note.
 */
export const ParticipantImportTargetSchema = z
  .string()
  .regex(
    /^(fullName|firstName|lastName|phone|email|gender|birthYear|birthDate|age|handicapIndex|nationality|isPro|bibNumber|note|group|teeTime|startHole|caddyCode|course|category:[A-Za-z0-9_-]+|custom:[A-Za-z0-9_]+)$/,
  );
/** ImportBatch.mapping: { [header]: target | null } */
export const ParticipantImportMappingSchema = z.record(
  z.string(),
  ParticipantImportTargetSchema.nullable(),
);
export type ParticipantImportMapping = z.infer<typeof ParticipantImportMappingSchema>;

export const ParticipantImportIssueSchema = z.object({
  field: z.string().nullable(),
  code: z.string(),
  message: z.string(),
});

export const ParticipantImportRowSchema = z.object({
  rowNumber: z.number(),
  raw: z.record(z.string(), z.string()),
  parsed: z.object({
    fullName: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    gender: Gender.nullable(),
    birthYear: z.number().nullable(),
    age: z.number().nullable(),
    handicapIndex: z.number().nullable(),
    nationality: z.string().nullable(),
    isPro: z.boolean().nullable(),
    bibNumber: z.number().nullable(),
    note: z.string().nullable(),
    categories: z.record(z.string(), z.string()),
    custom: z.record(z.string(), z.string()),
    /** group label of a pairing sheet ("TEAM 1", "A", "3조") — additive (§15 #463) */
    group: z.string().nullable().optional(),
    /** "HH:mm" of the group (additive) */
    teeTime: z.string().nullable().optional(),
    /** start hole of the group (additive) */
    startHole: z.number().nullable().optional(),
    /** first nine named next to the tee time ("11:40 Palm - Lotus" → "Palm"), additive */
    startNine: z.string().nullable().optional(),
    /** caddy number of the player (1 caddy per player, additive — §15 #471) */
    caddyCode: z.string().nullable().optional(),
    /**
     * course / layout of the group ("Lotus - Palm", "Sông Bé — Palm·Lotus", "Palm"): picks the
     * round layout and the nine the group starts on (additive — §15 #481)
     */
    course: z.string().nullable().optional(),
  }),
  errors: z.array(ParticipantImportIssueSchema),
  warnings: z.array(ParticipantImportIssueSchema),
  /** existing participant with the same phone (commit: update when updateExisting, else skip) */
  existingParticipantId: z.uuid().nullable(),
});
export type ParticipantImportRow = z.infer<typeof ParticipantImportRowSchema>;

export const ParticipantImportBatchSchema = z.object({
  id: z.uuid(),
  tournamentId: z.uuid(),
  fileName: z.string(),
  status: z.enum(['PREVIEW', 'COMMITTED', 'DISCARDED', 'FAILED']),
  headers: z.array(z.string()),
  mapping: ParticipantImportMappingSchema,
  /** available targets for the mapping UI */
  targets: z.array(z.object({ target: z.string(), label: z.string() })),
  rows: z.array(ParticipantImportRowSchema),
  rowCount: z.number(),
  errorCount: z.number(),
  warningCount: z.number(),
  /** rows matching an existing participant or repeating an earlier row of the file */
  duplicateCount: z.number(),
  /** 1-based file row used as the header (rows above it — title, blank rows — are skipped) */
  headerRow: z.number().optional(),
  /** header row choices for the preview (first non-empty rows); absent on older batches */
  headerRowOptions: z.array(z.object({ row: z.number(), preview: z.string() })).optional(),
  createdAt: z.iso.datetime(),
  committedAt: z.iso.datetime().nullable(),
});
export type ParticipantImportBatch = z.infer<typeof ParticipantImportBatchSchema>;

/** POST /tournaments/:tid/imports/paste (§15 #459) */
export const ParticipantImportPasteSchema = z.object({
  text: z.string().min(1).max(200_000),
});

export const ParticipantImportMappingUpdateSchema = z.object({
  /** omitted = keep the current mapping (ignored when headerRow changes: columns are re-mapped) */
  mapping: ParticipantImportMappingSchema.optional(),
  /** use this 1-based file row as the header instead of the detected one (re-maps the columns) */
  headerRow: z.number().int().min(1).optional(),
});

export const ParticipantImportRowEditSchema = z.object({
  rowNumber: z.number().int().min(0),
  exclude: z.boolean().optional(),
  fullName: z.string().trim().min(1).max(120).optional(),
  gender: Gender.nullable().optional(),
  handicapIndex: z.number().min(-10).max(54).nullable().optional(),
  /** null = no phone */
  phone: z.string().trim().max(40).nullable().optional(),
  /** group label ("" / null = no group) — additive (§15 #463) */
  group: z.string().trim().max(30).nullable().optional(),
});
export type ParticipantImportRowEdit = z.infer<typeof ParticipantImportRowEditSchema>;

export const ParticipantImportCommitSchema = z.object({
  /** commit valid rows and skip rows with errors (else 409 IMPORT_HAS_ERRORS) */
  skipErrors: z.boolean().default(false),
  status: z.enum(['APPROVED', 'PENDING']).default('APPROVED'),
  /** give approved rows without a bib column the next numbers */
  assignBibs: z.boolean().default(true),
  /** rows matching an existing participant (same phone): update it (true) or skip (false) */
  updateExisting: z.boolean().default(false),
  /**
   * 나의 대회 (§15 #459): also put the created players on the owner's "my members" list (same
   * phone or, without a phone, same name already there = skipped). Ignored outside the caller's
   * own personal space.
   */
  saveToMembers: z.boolean().default(false),
  /**
   * Corrections made on the preview, by file row (§15 #459): `exclude` leaves the row out; any
   * other edit takes the row in — sheet notes (ROW_CANCELLED), a missing name once one is given,
   * and unreadable gender / handicap / phone no longer stop it. Edited values replace the read
   * ones; the phone is normalised (400 PHONE_INVALID). Additive, default none.
   */
  edits: z.array(ParticipantImportRowEditSchema).max(2000).default([]),
  /**
   * Rows with a group (a pairing sheet's team / tee time / start hole, or "A팀" lines of a paste)
   * become groups of the first SCHEDULED round (§15 #463). Default true; false = players only.
   */
  createGroups: z.boolean().default(true),
});
export const ParticipantImportCommitResultSchema = z.object({
  created: z.number(),
  updated: z.number(),
  skipped: z.number(),
  /** players added to "my members" (saveToMembers) */
  savedMembers: z.number().optional(),
  /** groups created / players placed from the file's groups (createGroups) */
  groupsCreated: z.number().optional(),
  groupedPlayers: z.number().optional(),
  /** caddy accounts created / players linked from a "Caddie" column (additive, §15 #471) */
  caddiesCreated: z.number().optional(),
  caddiesLinked: z.number().optional(),
});

// ---- teams -----------------------------------------------------------------------------------
export const ParticipantTeamSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  number: z.number().nullable(),
  members: z.array(
    z.object({
      participantId: z.uuid(),
      fullName: z.string(),
      bibNumber: z.number().nullable(),
      handicapIndex: z.number().nullable(),
      position: z.number(),
    }),
  ),
});
export type ParticipantTeam = z.infer<typeof ParticipantTeamSchema>;
export const ParticipantTeamListSchema = z.object({ items: z.array(ParticipantTeamSchema) });

export const ParticipantTeamCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  number: z.int().min(1).max(9999).nullable().optional(),
  memberIds: z.array(z.uuid()).max(8).default([]),
});
export const ParticipantTeamUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  number: z.int().min(1).max(9999).nullable().optional(),
  memberIds: z.array(z.uuid()).max(8).optional(),
});
export const ParticipantTeamAutoSchema = z.object({
  size: z.int().min(2).max(4),
  strategy: z.enum(['RANDOM', 'HANDICAP_BALANCED', 'BY_GROUP']),
  /** delete existing teams first (only teams without scores / matches) */
  replace: z.boolean().default(false),
  seed: z.string().max(64).optional(),
  /** BY_GROUP: round whose groups are split into teams (default first round) */
  roundId: z.uuid().optional(),
});
export const ParticipantTeamAutoResultSchema = z.object({
  created: z.number(),
  seed: z.string(),
  items: z.array(ParticipantTeamSchema),
});

// =============================================================================================
// pairings
// =============================================================================================
export const PairingsMemberSchema = z.object({
  participantId: z.uuid(),
  position: z.number(),
  fullName: z.string(),
  bibNumber: z.number().nullable(),
  gender: Gender.nullable(),
  handicapIndex: z.number().nullable(),
  status: ParticipantStatus,
  playStatus: PlayStatus,
  teeBoxId: z.uuid().nullable(),
});
export const PairingsGroupSchema = z.object({
  id: z.uuid(),
  roundId: z.uuid(),
  courseId: z.uuid(),
  number: z.number(),
  name: z.string().nullable(),
  startHole: z.number(),
  teeTime: z.string().nullable(),
  members: z.array(PairingsMemberSchema),
  caddies: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string() })),
});
export type PairingsGroup = z.infer<typeof PairingsGroupSchema>;
export const PairingsGroupListSchema = z.object({
  roundId: z.uuid(),
  roundStatus: z.string(),
  items: z.array(PairingsGroupSchema),
  ungroupedCount: z.number(),
});

export const PairingsGroupCreateSchema = z.object({
  number: z.int().min(1).max(999).optional(),
  name: z.string().max(20).nullable().optional(),
  /** layout (RoundCourse) of the group; default Round.courseId */
  courseId: z.uuid().optional(),
  startHole: z.int().min(1).max(18).default(1),
  teeTime: HHMM.nullable().optional(),
  participantIds: z.array(z.uuid()).max(8).default([]),
  /** required when participantIds is non-empty and the round has started (audited) */
  reason: z.string().max(500).optional(),
});
export const PairingsGroupUpdateSchema = z.object({
  number: z.int().min(1).max(999).optional(),
  name: z.string().max(20).nullable().optional(),
  courseId: z.uuid().optional(),
  startHole: z.int().min(1).max(18).optional(),
  teeTime: HHMM.nullable().optional(),
  reason: z.string().max(500).optional(),
});
export const PairingsMembersSchema = z.object({
  participantIds: z.array(z.uuid()).max(8),
  /** required when the round is IN_PROGRESS or COMPLETED */
  reason: z.string().max(500).optional(),
});
export const PairingsMoveSchema = z.object({
  roundId: z.uuid(),
  /** null = remove from the round's groups */
  groupId: z.uuid().nullable(),
  position: z.int().min(0).max(7).optional(),
  reason: z.string().max(500).optional(),
});

export const PairingsAutoSchema = z.object({
  strategy: z.enum(['RANDOM', 'HANDICAP_ASC', 'HANDICAP_BALANCED', 'BY_CATEGORY']),
  groupSize: z.int().min(1).max(6).default(4),
  categoryKey: z.string().nullable().optional(),
  startType: StartType.optional(),
  firstTeeTime: HHMM.optional(),
  intervalMinutes: z.int().min(1).max(60).optional(),
  /** recorded for reproducibility (default: random) */
  seed: z.string().max(64).optional(),
  /** delete the round's groups first (refused when the round has scores) */
  replace: z.boolean().default(false),
  /** multi-layout rounds: groups per layout in order; the rest stays ungrouped */
  courseSplit: z
    .array(z.object({ courseId: z.uuid(), maxGroups: z.int().min(1).max(200) }))
    .min(1)
    .optional(),
  /** restrict to these participants (default: all APPROVED + ACTIVE not yet grouped) */
  participantIds: z.array(z.uuid()).optional(),
});
export const PairingsAutoResultSchema = z.object({
  seed: z.string(),
  created: z.number(),
  placed: z.number(),
  unplaced: z.number(),
  items: z.array(PairingsGroupSchema),
});

export const PairingsCopySchema = z.object({
  fromRoundId: z.uuid(),
  mode: z.enum(['SAME', 'BY_LEADERBOARD']),
  /** BY_LEADERBOARD: group size (default 4) */
  groupSize: z.int().min(1).max(6).default(4),
  replace: z.boolean().default(false),
  /**
   * BY_LEADERBOARD on multi-layout rounds: layouts to use (groups dealt round-robin in this
   * order, at most maxGroups each). Default: the round's primary course only.
   */
  courseSplit: z
    .array(z.object({ courseId: z.uuid(), maxGroups: z.int().min(1).max(200) }))
    .min(1)
    .optional(),
});
/** DELETE /groups/:groupId query: reason required once the round has started */
export const PairingsGroupDeleteQuerySchema = z.object({ reason: z.string().max(500).optional() });

export const PairingsStartSheetSchema = z.object({
  tournament: z.object({ id: z.uuid(), name: z.string(), timezone: z.string() }),
  round: z.object({
    id: z.uuid(),
    roundNo: z.number(),
    date: z.string(),
    startType: StartType,
    status: z.string(),
  }),
  courses: z.array(z.object({ id: z.uuid(), name: z.string() })),
  groups: z.array(
    z.object({
      id: z.uuid(),
      number: z.number(),
      name: z.string().nullable(),
      courseId: z.uuid(),
      courseName: z.string(),
      startHole: z.number(),
      teeTime: z.string().nullable(),
      players: z.array(
        z.object({
          participantId: z.uuid(),
          bibNumber: z.number().nullable(),
          fullName: z.string(),
          gender: Gender.nullable(),
          handicapIndex: z.number().nullable(),
          playStatus: PlayStatus,
        }),
      ),
      caddies: z.array(z.object({ code: z.string(), name: z.string() })),
    }),
  ),
});
export type PairingsStartSheet = z.infer<typeof PairingsStartSheetSchema>;

// =============================================================================================
// caddies
// =============================================================================================
export const CaddiesAccountSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  active: z.boolean(),
  failedAttempts: z.number(),
  pinResetRequired: z.boolean(),
  lastLoginAt: z.iso.datetime().nullable(),
  activeSessions: z.number(),
  assignments: z.array(
    z.object({
      groupId: z.uuid(),
      roundId: z.uuid(),
      roundNo: z.number(),
      groupNumber: z.number(),
      groupName: z.string().nullable(),
      /** additive: start hole and tee time of the group (caddy cards, start order) */
      startHole: z.number().optional(),
      teeTime: z.string().nullable().optional(),
    }),
  ),
  contestAssignments: z.array(z.object({ contestId: z.uuid(), roundId: z.uuid() })),
});
export type CaddiesAccount = z.infer<typeof CaddiesAccountSchema>;
export const CaddiesListSchema = z.object({ items: z.array(CaddiesAccountSchema) });

const caddyCode = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9-]{1,16}$/);
const caddyPin = z.string().regex(/^\d{6}$/, '6 digits');

export const CaddiesCreateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  phone: z.string().max(40).nullable().optional(),
  code: caddyCode.optional(),
  pin: caddyPin.optional(),
});
/** returned ONCE: the PIN is never shown again (only its argon2 hash is stored) */
export const CaddiesCreatedSchema = z.object({
  caddy: CaddiesAccountSchema,
  pin: z.string(),
});
export const CaddiesBulkSchema = z.object({
  count: z.int().min(1).max(200),
  namePrefix: z.string().trim().min(1).max(60).default('Caddy'),
});
export const CaddiesBulkResultSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string(), pin: z.string() })),
});
export const CaddiesUpdateSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  phone: z.string().max(40).nullable().optional(),
  active: z.boolean().optional(),
});
export const CaddiesResetPinSchema = z.object({ pin: caddyPin.optional() });
export const CaddiesPinResultSchema = z.object({ code: z.string(), pin: z.string() });
export const CaddiesDeleteResultSchema = z.object({
  deleted: z.boolean(),
  /** caddies with recorded entries are deactivated instead */
  active: z.boolean(),
});
/**
 * Replaces the caddy's assignments in SCHEDULED / IN_PROGRESS rounds. Assignments in COMPLETED /
 * LOCKED rounds are kept when not listed; adding one in a COMPLETED round needs `reason`, in a
 * LOCKED round is refused (409 ROUND_LOCKED).
 */
export const CaddiesAssignmentsSchema = z.object({
  groupIds: z.array(z.uuid()).max(100),
  reason: z.string().max(500).optional(),
});
/** COMPLETED round: reason required; LOCKED round: 409 ROUND_LOCKED */
export const CaddiesGroupCaddiesSchema = z.object({
  caddyIds: z.array(z.uuid()).max(20),
  reason: z.string().max(500).optional(),
});
export const CaddiesAutoAssignSchema = z.object({
  /** clear the round's existing assignments first */
  replace: z.boolean().default(false),
});
export const CaddiesAutoAssignResultSchema = z.object({
  assigned: z.number(),
  unassignedGroups: z.number(),
});
export const CaddiesContestAssignmentsSchema = z.object({
  items: z.array(z.object({ contestId: z.uuid(), roundId: z.uuid() })).max(100),
});

// =============================================================================================
// matchplay
// =============================================================================================
/** Match.extraHoles — sudden-death results entered by an admin, in order */
export const MatchplayExtraHolesSchema = z
  .array(z.object({ hole: z.int().min(1).max(18), winner: z.enum(['A', 'B', 'HALVED']) }))
  .max(18);
export type MatchplayExtraHoles = z.infer<typeof MatchplayExtraHolesSchema>;

const sideRef = z
  .object({ participantId: z.uuid().optional(), teamId: z.uuid().optional() })
  .refine((s) => Boolean(s.participantId) !== Boolean(s.teamId), {
    message: 'exactly one of participantId / teamId',
  });

export const MatchplaySideSchema = z.object({
  participantId: z.uuid().nullable(),
  teamId: z.uuid().nullable(),
  /** competitor id (participantId or teamId), null = TBD / bye */
  id: z.string().nullable(),
  name: z.string(),
  bye: z.boolean(),
  /** Participant.nationality (ISO code or free text) of an individual side; absent from older servers */
  nationality: z.string().nullable().optional(),
  /** bracket seed number (1 = top seed, Bracket.seeds order); null outside a bracket / for TBD */
  seed: z.number().int().nullable().optional(),
});
export const MatchplayMatchSchema = z.object({
  id: z.uuid(),
  bracketId: z.uuid().nullable(),
  bracketRound: z.number().nullable(),
  bracketIndex: z.number().nullable(),
  roundId: z.uuid().nullable(),
  roundNo: z.number().nullable(),
  groupId: z.uuid().nullable(),
  status: z.enum(['SCHEDULED', 'IN_PROGRESS', 'COMPLETE']),
  winnerSide: z.enum(['A', 'B']).nullable(),
  halved: z.boolean(),
  resultText: z.string().nullable(),
  extraHoles: MatchplayExtraHolesSchema.nullable(),
  decided: z.boolean(),
  decisionNote: z.string().nullable(),
  sideA: MatchplaySideSchema,
  sideB: MatchplaySideSchema,
  /**
   * Live state computed from the scores (admin view: statusCode, playoff, handicap strokes); null
   * when it cannot be computed, absent from older servers.
   */
  live: MatchViewSchema.nullable().optional(),
});
export type MatchplayMatch = z.infer<typeof MatchplayMatchSchema>;
export const MatchplayMatchListSchema = z.object({ items: z.array(MatchplayMatchSchema) });

export const MatchplayBracketSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  size: z.number(),
  seeds: z.array(z.string()),
  matchCount: z.number(),
  createdAt: z.iso.datetime(),
});
export const MatchplayBracketListSchema = z.object({
  items: z.array(MatchplayBracketSummarySchema),
});
export const MatchplayBracketDetailSchema = MatchplayBracketSummarySchema.extend({
  rounds: z.number(),
  seedNames: z.array(z.object({ id: z.string(), name: z.string(), seed: z.number() })),
  slots: z.array(
    z.object({
      round: z.number(),
      index: z.number(),
      matchId: z.uuid().nullable(),
      sideA: MatchplaySideSchema,
      sideB: MatchplaySideSchema,
      winnerSide: z.enum(['A', 'B']).nullable(),
      status: z.enum(['SCHEDULED', 'IN_PROGRESS', 'COMPLETE']),
      resultText: z.string().nullable(),
    }),
  ),
  matches: z.array(MatchplayMatchSchema),
});
export type MatchplayBracketDetail = z.infer<typeof MatchplayBracketDetailSchema>;

export const MatchplayBracketCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    /** competitor ids (participants for singles, teams for foursome/fourball) in seed order */
    seeds: z.array(z.uuid()).min(2).max(128).optional(),
    seedBy: z.enum(['HANDICAP', 'QUALIFYING', 'RANDOM']).optional(),
    /** pool for seedBy (default: every APPROVED participant / every team) */
    competitorIds: z.array(z.uuid()).min(2).max(128).optional(),
    /** tournament round in which the first bracket round is played */
    roundId: z.uuid().nullable().optional(),
    seed: z.string().max(64).optional(),
  })
  .refine((b) => Boolean(b.seeds) !== Boolean(b.seedBy), {
    message: 'give either seeds[] or seedBy',
  });

export const MatchplayMatchCreateSchema = z.object({
  sideA: sideRef,
  sideB: sideRef,
  roundId: z.uuid().nullable().optional(),
  groupId: z.uuid().nullable().optional(),
});
export const MatchplayMatchUpdateSchema = z.object({
  roundId: z.uuid().nullable().optional(),
  groupId: z.uuid().nullable().optional(),
});
export const MatchplayDecideSchema = z.object({
  extraHoles: MatchplayExtraHolesSchema.optional(),
  winnerSide: z.enum(['A', 'B']).optional(),
  halved: z.boolean().optional(),
  /** the loser conceded the match (winnerSide required) */
  conceded: z.boolean().optional(),
  /** decision note — required */
  note: z.string().max(500),
});
